import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type TopCommunitiesBySentimentView = {
  /** `Most hostile`. */
  kpis: KPIMetric[];
  /** One row per community (same order as `Top communities by mention`), one key per sentiment. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any community row is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type TopCommunitiesBySentimentCardProps = WidgetBlockProps & {
  view?: TopCommunitiesBySentimentView;
  /** Opens the conversations behind a community (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19575` — the tone mix inside each community.
 *
 * Normalised, so the card reads as composition — "what is this community made
 * of" rather than "how big is it", which is the question `Top communities by
 * mention` already answers off the same rows.
 *
 * Ranked by total mentions rather than by hostility, so the bars appear in the
 * same order on both. Two cards on one page showing the same five categories in
 * two different orders costs a reader more than the ranking is worth, and the
 * figure above names the hostile one.
 */
export function TopCommunitiesBySentimentCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TopCommunitiesBySentimentCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No community conversation in this period"
      {...cardState(status, hasData, onRetry)}
      title="Top communities by sentiment"
      helpText="The positive, neutral and negative sentiment mix within each monitored community. Each bar shows percentages rather than mention volume."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            normalize
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
